import { useState, useCallback, useEffect } from 'react';
import { getBillsByNumber } from '../../apis/getBillsByNumber';
import { deleteDocument } from '../../apis/deleteDocument';
import {
  fetchDocumentsForInvoice,
  handleFileUpload,
} from '../../utils/invoice.utils';
import {
  InvoiceWithDocuments,
  MessageState,
  TableDataType,
} from '../../types/invoice.types';
import { InvoiceData } from '../../components/layout/bodyLayout/BodyLayout';
import { UseAuth } from '../../contexts/AuthContext';

interface InvoiceFormState {
  selectedClient: string;
  selectedMatter: string;
  billNumber: string;
  selectedBillNumber: string | null;
  showMatterDropdown: boolean;
  fileUpload: {
    files: File[];
    isValid: boolean;
  };
  invoicesWithDocs: InvoiceWithDocuments[];
  fileInputKey: number;
}

interface LoadingState {
  isSearching: boolean;
  isUploading: boolean;
  isDeleting: boolean;
  isLoadingDocs: boolean;
}

export const useInvoiceForm = (
  onClientSelect: (client: string) => void,
  onClientSearch: (value: string) => void,
  onMatterSelect: (matter: string) => void,
  invoiceData: InvoiceData[] | null
) => {
  // State
  const { getAccessToken } = UseAuth();
  const [formState, setFormState] = useState<InvoiceFormState>({
    selectedClient: '',
    selectedMatter: '',
    billNumber: '',
    selectedBillNumber: null,
    showMatterDropdown: false,
    fileUpload: {
      files: [],
      isValid: false,
    },
    invoicesWithDocs: [],
    fileInputKey: 0,
  });

  const [loadingState, setLoadingState] = useState<LoadingState>({
    isSearching: false,
    isUploading: false,
    isDeleting: false,
    isLoadingDocs: false,
  });

  const [messageState, setMessageState] = useState<MessageState>({
    search: null,
    upload: null,
    delete: null,
    docs: null,
    success: null,
  });

  // Effect to handle invoiceData changes from BodyLayout
  useEffect(() => {
    const fetchDocumentsForInvoices = async () => {
      if (!invoiceData) {
        setFormState((prev) => ({
          ...prev,
          invoicesWithDocs: [],
        }));
        return;
      }

      setLoadingState((prev) => ({ ...prev, isLoadingDocs: true }));
      setMessageState((prev) => ({ ...prev, docs: null }));

      try {
        const invoicesWithDocuments = await Promise.all(
          invoiceData.map(async (invoice) => {
            const documents = await fetchDocumentsForInvoice(
              invoice.BILL_NUM.toString(),
              getAccessToken
            );
            return {
              ...invoice,
              documents,
              selectedDocuments: [],
            };
          })
        );

        setFormState((prev) => ({
          ...prev,
          invoicesWithDocs: invoicesWithDocuments,
        }));
      } catch (error) {
        console.error('Error fetching documents:', error);
        setMessageState((prev) => ({
          ...prev,
          docs: 'Failed to fetch documents',
        }));
      } finally {
        setLoadingState((prev) => ({ ...prev, isLoadingDocs: false }));
      }
    };

    fetchDocumentsForInvoices();
  }, [invoiceData]);

  useEffect(() => {
    if (!formState.selectedClient) {
      setFormState((prev) => ({
        ...prev,
        selectedMatter: '',
        showMatterDropdown: false,
        billNumber: '',
        selectedBillNumber: null,
        invoicesWithDocs: [],
      }));
      setMessageState({
        search: null,
        upload: null,
        delete: null,
        docs: null,
        success: null,
      });
    }
  }, [formState.selectedClient]);

  const clearSuccessMessage = useCallback(() => {
    setTimeout(() => {
      setMessageState((prev) => ({ ...prev, success: null }));
    }, 3000); // Clear after 3 seconds
  }, []);

  // Bill Number Handlers
  const setBillNumber = useCallback((value: string) => {
    setFormState((prev) => ({
      ...prev,
      billNumber: value,
    }));
    setMessageState((prev) => ({
      ...prev,
      search: null,
    }));
  }, []);

  const handleBillNumberSearch = useCallback(async () => {
    if (!formState.billNumber.trim()) {
      setMessageState((prev) => ({
        ...prev,
        search: 'Please enter a bill number',
      }));
      return;
    }

    setLoadingState((prev) => ({ ...prev, isSearching: true }));
    setMessageState((prev) => ({ ...prev, search: null }));

    try {
      const response = await getBillsByNumber({
        billNumber: formState.billNumber,
        getAccessToken,
      });

      if (response?.success && response.data) {
        const invoiceDataArray = Array.isArray(response.data)
          ? response.data
          : [response.data as InvoiceData];

        setFormState((prev) => ({
          ...prev,
          selectedClient: '',
          selectedMatter: '',
          showMatterDropdown: false,
          selectedBillNumber: formState.billNumber,
        }));

        const invoicesWithDocuments = await Promise.all(
          invoiceDataArray.map(async (invoice) => {
            const documents = await fetchDocumentsForInvoice(
              invoice.BILL_NUM.toString(),
              getAccessToken,
            );
            return {
              ...invoice,
              documents,
              selectedDocuments: [],
            };
          })
        );

        setFormState((prev) => ({
          ...prev,
          invoicesWithDocs: invoicesWithDocuments,
        }));
      } else {
        setMessageState((prev) => ({
          ...prev,
          search: response?.error || 'No invoices found',
        }));
        setFormState((prev) => ({
          ...prev,
          selectedBillNumber: null,
        }));
      }
    } catch (error) {
      setMessageState((prev) => ({
        ...prev,
        search: 'Failed to search for bill number',
      }));
      console.error('Error searching bill number:', error);
      setFormState((prev) => ({
        ...prev,
        selectedBillNumber: null,
      }));
    } finally {
      setLoadingState((prev) => ({ ...prev, isSearching: false }));
    }
  }, [formState.billNumber]);

  // Document Selection Handlers
  const handleDocumentSelection = useCallback(
    (billNumber: string, documentId: string, isSelected: boolean) => {
      setFormState((prev) => ({
        ...prev,
        invoicesWithDocs: prev.invoicesWithDocs.map((invoice) => {
          if (invoice.BILL_NUM.toString() === billNumber) {
            return {
              ...invoice,
              selectedDocuments: isSelected
                ? [...invoice.selectedDocuments, documentId]
                : invoice.selectedDocuments.filter((id) => id !== documentId),
            };
          }
          return invoice;
        }),
      }));
    },
    []
  );

  const handleDeleteSelectedDocuments = useCallback(async () => {
    const selectedDocs = formState.invoicesWithDocs.flatMap((invoice) =>
      invoice.selectedDocuments.map((documentId) => documentId)
    );

    if (selectedDocs.length === 0) {
      setMessageState((prev) => ({
        ...prev,
        delete: 'Please select documents to delete',
      }));
      return;
    }

    if (
      !confirm(
        `Are you sure you want to delete ${selectedDocs.length} document(s)?`
      )
    ) {
      return;
    }

    setLoadingState((prev) => ({ ...prev, isDeleting: true }));
    setMessageState((prev) => ({ ...prev, delete: null }));

    try {
      const deletionResults = await Promise.allSettled(
        selectedDocs.map((documentId) => deleteDocument({ documentId: documentId, getAccessToken }))
      );

      // Initialize counters and message collections
      const results = {
        posted: [] as string[],
        successful: [] as string[],
        failed: [] as string[],
        messages: new Set<string>(), // Collect unique error messages
      };

      // Process each deletion result
      deletionResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const response = result.value;
          const documentId = selectedDocs[index];

          // Check for "Document Already Posted" message
          if (response.data?.message?.includes('Document already posted')) {
            results.posted.push(documentId);
            results.messages.add(
              'Some documents cannot be deleted as they have already been posted'
            );
          }
          // Check for other success/error messages
          else if (response.success && !response.data?.message) {
            results.successful.push(documentId);
          }
          // Handle other server messages
          else if (response.data?.message) {
            results.failed.push(documentId);
            results.messages.add(response.data.message);
          }
          // Handle generic failure
          else {
            results.failed.push(documentId);
            results.messages.add('Failed to delete document');
          }
        } else {
          // Handle promise rejection
          results.failed.push(selectedDocs[index]);
          results.messages.add('Server error occurred during deletion');
        }
      });

      // Refresh documents list
      const updatedInvoices = await Promise.all(
        formState.invoicesWithDocs.map(async (invoice) => ({
          ...invoice,
          documents: await fetchDocumentsForInvoice(
            invoice.BILL_NUM.toString(),
            getAccessToken
          ),
          selectedDocuments: [],
        }))
      );

      setFormState((prev) => ({
        ...prev,
        invoicesWithDocs: updatedInvoices,
      }));

      // Construct status message
      const statusParts: string[] = [];

      if (results.posted.length > 0) {
        statusParts.push(
          `${results.posted.length} document(s) cannot be deleted as they have already been posted`
        );
      }

      if (results.successful.length > 0) {
        statusParts.push(
          `${results.successful.length} document(s) deleted successfully`
        );
      }

      if (results.failed.length > 0) {
        statusParts.push(
          `Failed to delete ${results.failed.length} document(s)`
        );
      }

      // Add any additional server messages
      const additionalMessages = Array.from(results.messages).filter(
        (msg) =>
          !msg.includes('Document already posted') &&
          !msg.includes('Failed to delete document') &&
          !msg.includes('Server error occurred')
      );

      if (additionalMessages.length > 0) {
        statusParts.push(...additionalMessages);
      }

      setMessageState((prev) => ({
        ...prev,
        delete: statusParts.join('. '),
      }));
    } catch (error) {
      console.error('Error deleting documents:', error);
      setMessageState((prev) => ({
        ...prev,
        delete: 'Failed to delete documents',
      }));
    } finally {
      setLoadingState((prev) => ({ ...prev, isDeleting: false }));
    }
  }, [formState.invoicesWithDocs]);

  // Client/Matter Handlers
  const handleClientSelect = useCallback(
    (option: string) => {
      setFormState((prev) => ({
        ...prev,
        selectedClient: option,
        selectedMatter: !option ? '' : prev.selectedMatter,
        showMatterDropdown: !!option,
        billNumber: '',
        selectedBillNumber: null,
        invoicesWithDocs: [],
      }));
      setMessageState({
        search: null,
        upload: null,
        delete: null,
        docs: null,
        success: null,
      });
      onClientSelect(option);
    },
    [onClientSelect]
  );

  const handleClientSearch = useCallback(
    (value: string) => {
      if (!value.trim()) {
        setFormState((prev) => ({
          ...prev,
          selectedBillNumber: null,
          selectedClient: '',
          selectedMatter: '',
          showMatterDropdown: false,
          invoicesWithDocs: [],
          billNumber: '',
        }));
        setMessageState({
          search: null,
          upload: null,
          delete: null,
          docs: null,
          success: null,
        });
      }
      onClientSearch(value);
    },
    [onClientSearch]
  );

  const handleMatterSelect = useCallback(
    (option: string) => {
      setFormState((prev) => ({
        ...prev,
        selectedMatter: option,
        showMatterDropdown: false,
        billNumber: '',
        selectedBillNumber: null,
      }));
      setMessageState({
        search: null,
        upload: null,
        delete: null,
        docs: null,
        success: null,
      });
      onMatterSelect(option);
    },
    [onMatterSelect]
  );

  // File Handling
  const handleFileSelect = useCallback((files: File[]) => {
    setFormState((prev) => ({
      ...prev,
      fileUpload: {
        files,
        isValid: files.length > 0,
      },
    }));
    setMessageState((prev) => ({
      ...prev,
      upload: null,
    }));
  }, []);

  const handleUploadClick = useCallback(async () => {
    if (!formState.fileUpload.files.length || !formState.selectedBillNumber) {
      setMessageState((prev) => ({
        ...prev,
        upload: 'Please select a bill number and files before uploading',
        success: null,
      }));
      return;
    }

    setLoadingState((prev) => ({ ...prev, isUploading: true }));
    setMessageState((prev) => ({
      ...prev,
      upload: null,
      success: null,
    }));

    try {
      const result = await handleFileUpload(
        formState.fileUpload.files,
        formState.selectedBillNumber,
        getAccessToken,
      );

      if (result.success) {
        const updatedDocs = await fetchDocumentsForInvoice(
          formState.selectedBillNumber,
          getAccessToken
        );

        setFormState((prev) => ({
          ...prev,
          fileUpload: { files: [], isValid: false },
          fileInputKey: prev.fileInputKey + 1,
          invoicesWithDocs: prev.invoicesWithDocs.map((invoice) =>
            invoice.BILL_NUM.toString() === formState.selectedBillNumber
              ? { ...invoice, documents: updatedDocs }
              : invoice
          ),
        }));

        // Set success message
        setMessageState((prev) => ({
          ...prev,
          success: `Successfully uploaded ${formState.fileUpload.files.length} file(s)`,
          upload: null,
        }));

        // Clear success message after delay
        clearSuccessMessage();

        // Clear file input
        const fileInput = document.querySelector(
          'input[type="file"]'
        ) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      } else {
        setMessageState((prev) => ({
          ...prev,
          upload: result.error || 'Upload failed',
          success: null,
        }));
      }
    } catch (error) {
      console.error('Upload error:', error);
      setMessageState((prev) => ({
        ...prev,
        upload: 'Failed to upload files',
        success: null,
      }));
    } finally {
      setLoadingState((prev) => ({ ...prev, isUploading: false }));
    }
  }, [
    formState.fileUpload.files,
    formState.selectedBillNumber,
    formState.selectedClient,
    formState.selectedMatter,
    clearSuccessMessage,
  ]);

  // Row Click Handler
  const handleRowClick = useCallback((invoice: TableDataType) => {
    setFormState((prev) => ({
      ...prev,
      selectedBillNumber: invoice.billNumber,
    }));
    setMessageState((prev) => ({
      ...prev,
      upload: null,
    }));
  }, []);

  return {
    formState,
    loadingState,
    messageState,
    handlers: {
      handleBillNumberSearch,
      handleDocumentSelection,
      handleDeleteSelectedDocuments,
      handleClientSelect,
      handleClientSearch,
      handleMatterSelect,
      handleFileSelect,
      handleUploadClick,
      handleRowClick,
      setBillNumber,
    },
  };
};
